import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  applyRecurringMonth,
  type Category,
  type Expense,
  type RecurringMonthEntry,
  type RecurringTemplate,
} from '../api';
import { CategoryIcon } from '../categoryIcons';
import { amountToInputString, parseAmountInput } from '../utils/money';

export type RecurringRowState = {
  recurringId: string;
  enabled: boolean;
  amountInput: string;
};

type Props = {
  open: boolean;
  year: number;
  monthIndex: number;
  templates: RecurringTemplate[];
  categoryById: Map<string, Category>;
  expenses: Expense[];
  onClose: () => void;
  onSaved: () => void;
};

function buildInitialRows(
  templates: RecurringTemplate[],
  expenses: Expense[]
): RecurringRowState[] {
  const active = templates.filter((t) => t.active !== false);
  const instanceByRecurring = new Map<string, Expense>();
  for (const ex of expenses) {
    if (ex.recurringId) instanceByRecurring.set(ex.recurringId, ex);
  }
  return active.map((t) => {
    const inst = instanceByRecurring.get(t.recurringId);
    return {
      recurringId: t.recurringId,
      enabled: Boolean(inst),
      amountInput: amountToInputString(inst ? Number(inst.amount) || 0 : t.defaultAmount),
    };
  });
}

export function RecurringMonthModal({
  open,
  year,
  monthIndex,
  templates,
  categoryById,
  expenses,
  onClose,
  onSaved,
}: Props) {
  const [rows, setRows] = useState<RecurringRowState[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const activeTemplates = useMemo(
    () => templates.filter((t) => t.active !== false),
    [templates]
  );

  const templateById = useMemo(() => {
    const m = new Map<string, RecurringTemplate>();
    for (const t of templates) m.set(t.recurringId, t);
    return m;
  }, [templates]);

  useEffect(() => {
    if (open) {
      setRows(buildInitialRows(templates, expenses));
      setErr('');
    }
  }, [open, templates, expenses]);

  if (!open) return null;

  const monthNum = monthIndex + 1;

  function setRow(recurringId: string, patch: Partial<RecurringRowState>) {
    setRows((prev) => prev.map((r) => (r.recurringId === recurringId ? { ...r, ...patch } : r)));
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    const entries: RecurringMonthEntry[] = [];
    for (const row of rows) {
      if (!row.enabled) {
        entries.push({ recurringId: row.recurringId, enabled: false });
        continue;
      }
      const amount = parseAmountInput(row.amountInput);
      if (amount == null) {
        const name = templateById.get(row.recurringId)?.name ?? 'voce';
        setErr(`Importo non valido per «${name}».`);
        return;
      }
      entries.push({ recurringId: row.recurringId, enabled: true, amount });
    }

    setBusy(true);
    try {
      await applyRecurringMonth({ year, month: monthNum, entries });
      onSaved();
      onClose();
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'Salvataggio non riuscito');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget && !busy) onClose();
      }}
    >
      <div className="modal-panel recurring-month-modal" role="dialog" aria-labelledby="recurring-month-title">
        <h3 id="recurring-month-title" className="modal-title">
          Movimenti ricorrenti del mese
        </h3>
        <p className="recurring-month-lead home-muted">
          Attiva entrate e uscite da inserire in elenco. Puoi modificare solo l&apos;importo; la descrizione resta
          quella dell&apos;anagrafica.
        </p>

        {activeTemplates.length === 0 ? (
          <p className="home-muted">
            Nessun movimento attivo in anagrafica.{' '}
            <Link to="/ricorrenti" onClick={onClose}>
              Gestisci anagrafica
            </Link>
          </p>
        ) : (
          <form onSubmit={(e) => void onSave(e)}>
            <ul className="recurring-month-list">
              {rows.map((row) => {
                const t = templateById.get(row.recurringId);
                if (!t) return null;
                const cat = categoryById.get(t.categoryId);
                const isIn = cat?.type === 'in';
                return (
                  <li key={row.recurringId} className="recurring-month-row">
                    <label className="recurring-month-check">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) => setRow(row.recurringId, { enabled: e.target.checked })}
                        disabled={busy}
                      />
                      <CategoryIcon
                        iconId={cat?.icon}
                        size={20}
                        className="recurring-month-cat-icon"
                      />
                      <span className="recurring-month-name">{t.name}</span>
                      <span className={`category-picker-pill category-picker-pill--${isIn ? 'in' : 'out'}`}>
                        {isIn ? 'Entrata' : 'Uscita'}
                      </span>
                    </label>
                    <div className="recurring-month-amount-wrap">
                      <label className="sr-only" htmlFor={`rec-amt-${row.recurringId}`}>
                        Importo {t.name}
                      </label>
                      <input
                        id={`rec-amt-${row.recurringId}`}
                        type="text"
                        inputMode="decimal"
                        className="recurring-month-amount"
                        value={row.amountInput}
                        onChange={(e) => setRow(row.recurringId, { amountInput: e.target.value })}
                        disabled={busy || !row.enabled}
                        placeholder="Importo"
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
            {err && <p className="field-err">{err}</p>}
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                Annulla
              </button>
              <button type="submit" className="btn-primary btn-primary-inline" disabled={busy}>
                {busy ? '…' : 'Salva'}
              </button>
            </div>
          </form>
        )}

        <p className="recurring-month-footer">
          <Link to="/ricorrenti" className="btn-link" onClick={onClose}>
            Gestisci anagrafica
          </Link>
        </p>
      </div>
    </div>
  );
}
